import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import PageHeading from "../common/PageHeading/PageHeading";
import Loader from "../Loader";
import useFetch from "../../utils/hooks/useFetch";
import api from "../../api";
import { reportEndpoints } from "../../api/endpoints/report.endpoints";
import { benchmarkingApis } from "../../api/benchmarkingApis";
import { formSchemaApis } from "../../api/formSchemaApis";
import { submissionApis } from "../../api/submissionApis";
import { hydrateReportFromDynamicKpis } from "../../utils/hydrateReportFromDynamicKpis";
import {
  asDisplayLabel,
  buildBenchmarkPayload,
  buildBenchmarkDashboard,
  generateGapInsights,
} from "../../utils/benchmarking/benchmarkEngine";
import { BENCHMARK_TYPES } from "../../constants/benchmarkTypes";
import BenchmarkDashboard from "./BenchmarkDashboard";
import SectionBarComparison from "./SectionBarComparison";
import SectionComparisonChart from "./SectionComparisonChart";
import KpiComparisonTable from "./KpiComparisonTable";
import BenchmarkInsights from "./BenchmarkInsights";
import CustomBenchmarkPanel from "./CustomBenchmarkPanel";
import RequestBenchmarkPanel from "./RequestBenchmarkPanel";
import SiRatingPanel from "./siRating/SiRatingPanel";
import "./benchmarking.css";

const KPI_SECTION_TABS = [
  { id: "all", label: "All KPIs" },
  { id: "environment", label: "Environmental" },
  { id: "social", label: "Social" },
  { id: "governance", label: "Governance" },
  { id: "base", label: "Base" },
];

const BENCHMARKABLE_STATUSES = ["ready", "sendForVerification", "vetted"];

function isScoredReport(report) {
  return report?.score != null || Boolean(report?.grade);
}

function toReportOption(report) {
  if (!report) return null;
  const id = report.id || report._id;
  if (!id) return null;
  return {
    id: String(id),
    name: report.name,
    year: report.year,
    grade: report.grade,
    score: report.score,
    status: report.status,
  };
}

function errorMessage(err, fallback) {
  return (
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

export default function BenchmarkingPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedReportId, setSelectedReportId] = useState(params.reportId || "");
  const [benchmarkType, setBenchmarkType] = useState(
    searchParams.get("type") || "industry_average",
  );
  const [kpiTab, setKpiTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [customSectionTargets, setCustomSectionTargets] = useState(null);
  const [error, setError] = useState("");

  const {
    data: reportsData,
    loading: reportsLoading,
    error: reportsError,
  } = useFetch(reportEndpoints.getReports, {
    status: BENCHMARKABLE_STATUSES,
  });

  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");

  useEffect(() => {
    if (!selectedReportId) {
      setReportData(null);
      setReportError("");
      return;
    }
    let active = true;
    setReportLoading(true);
    setReportError("");
    api
      .get(reportEndpoints.getReport(selectedReportId))
      .then((res) => {
        if (active) setReportData(res.data);
      })
      .catch((err) => {
        if (active) {
          setReportData(null);
          setReportError(errorMessage(err, "Failed to load report"));
        }
      })
      .finally(() => {
        if (active) setReportLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedReportId]);

  const mappedReport = useMemo(
    () =>
      reportData?.report ? hydrateReportFromDynamicKpis(reportData.report) : null,
    [reportData?.report],
  );

  const scoredReports = useMemo(() => {
    const byId = new Map();
    (reportsData?.reports || []).forEach((report) => {
      if (!BENCHMARKABLE_STATUSES.includes(report.status)) return;
      if (!isScoredReport(report)) return;
      const option = toReportOption(report);
      if (option) byId.set(option.id, option);
    });

    const current = toReportOption(mappedReport);
    if (current && !byId.has(current.id)) {
      byId.set(current.id, current);
    }

    return Array.from(byId.values());
  }, [reportsData, mappedReport]);

  useEffect(() => {
    if (!selectedReportId && scoredReports.length > 0) {
      setSelectedReportId(scoredReports[0].id);
    }
  }, [scoredReports, selectedReportId]);

  useEffect(() => {
    if (params.reportId && params.reportId !== selectedReportId) {
      setSelectedReportId(params.reportId);
    }
  }, [params.reportId]);

  const selectValue = scoredReports.some((r) => r.id === String(selectedReportId))
    ? String(selectedReportId)
    : "";

  const listError = reportsError
    ? errorMessage(reportsError, "Failed to load reports")
    : reportError;

  const isRequestFlow = benchmarkType === "request_peer_companies";
  const isSiRating = searchParams.get("mode") === "si-rating";

  const toggleSiRating = () => {
    if (isSiRating) {
      setSearchParams({ type: benchmarkType });
    } else {
      setSearchParams({ mode: "si-rating" });
    }
  };

  const loadAnalysis = useCallback(async () => {
    if (!selectedReportId || !mappedReport) return;
    if (benchmarkType === "request_peer_companies") {
      setAnalysis(null);
      setLoading(false);
      setError("");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let payload = null;

      try {
        const apiRes = await benchmarkingApis.getAnalysis(
          selectedReportId,
          benchmarkType,
          benchmarkType === "custom" ? { sections: customSectionTargets } : {},
        );
        if (apiRes?.success && apiRes?.data) {
          payload = apiRes.data;
          if (!payload.dashboard) {
            payload.dashboard = buildBenchmarkDashboard(mappedReport, payload);
          }
        }
      } catch {
        // Fall back to client-side engine when API unavailable
      }

      if (!payload) {
        const sector =
          mappedReport.formSchemaSector ||
          mappedReport.sector?.name ||
          mappedReport.organizationDetails?.organization?.sector?.name;

        const schemas = sector
          ? await formSchemaApis.getSchemasBySector(sector)
          : [];
        const schemasArray = Array.isArray(schemas)
          ? schemas
          : schemas?.items || schemas?.data || [];

        const submissions = await submissionApis.getSubmissionsByReport(selectedReportId);

        payload = buildBenchmarkPayload({
          report: mappedReport,
          schemas: schemasArray,
          submissions,
          benchmarkType,
          customSectionTargets: benchmarkType === "custom" ? customSectionTargets : undefined,
        });
        payload.insights = generateGapInsights(payload);
        payload.report = {
          id: selectedReportId,
          name: mappedReport.name,
          year: mappedReport.year,
          grade: mappedReport.grade,
          score: mappedReport.score,
          sector,
        };
        if (!payload.dashboard) {
          payload.dashboard = buildBenchmarkDashboard(mappedReport, payload);
        }
      }

      setAnalysis(payload);
    } catch (err) {
      setError(err?.message || "Failed to load benchmark analysis");
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  }, [selectedReportId, mappedReport, benchmarkType, customSectionTargets]);

  useEffect(() => {
    if (mappedReport && selectedReportId) {
      loadAnalysis();
    }
  }, [loadAnalysis, mappedReport, selectedReportId]);

  const handleBenchmarkTypeChange = (type) => {
    setBenchmarkType(type);
    setSearchParams({ type });
  };

  const gapClass =
    analysis?.overall?.gap >= 2
      ? "ahead"
      : analysis?.overall?.gap <= -2
        ? "behind"
        : "on_par";

  const dashboard = analysis?.dashboard;
  const orgName =
    mappedReport?.organizationDetails?.organization?.name ||
    analysis?.report?.name;

  const showCustomPanel = benchmarkType === "custom";
  const requestReport = useMemo(
    () =>
      mappedReport
        ? {
            id: selectedReportId,
            name: mappedReport.name,
            year: mappedReport.year,
            sector:
              mappedReport.formSchemaSector ||
              mappedReport.sector?.name ||
              mappedReport.organizationDetails?.organization?.sector?.name ||
              "",
          }
        : selectedReportId
          ? { id: selectedReportId }
          : null,
    [mappedReport, selectedReportId],
  );

  const requestPeerType = BENCHMARK_TYPES.find(
    (t) => t.id === "request_peer_companies",
  );
  const analysisTypes = BENCHMARK_TYPES.filter(
    (t) => t.id !== "request_peer_companies",
  );

  return (
    <div className="benchmarking-page">
      <div className="benchmarking-header">
        <div className="benchmarking-header__title-row">
          <PageHeading onClick={() => navigate("/")} text="Benchmark Dashboard" />
          <button
            type="button"
            className={`benchmark-request-peers-btn${isSiRating ? " is-active" : ""}`}
            onClick={toggleSiRating}
            title={isSiRating ? "Back to benchmark analysis" : "Score SDM data and old reports into an SI Rating"}
            aria-pressed={isSiRating}
          >
            {isSiRating ? <CloseRoundedIcon fontSize="small" /> : <WorkspacePremiumOutlinedIcon fontSize="small" />}
            <span className="benchmark-request-peers-btn__label">
              {isSiRating ? "Close SI Rating" : "SI Rating"}
            </span>
          </button>
          {requestPeerType && !isSiRating ? (
            <button
              type="button"
              className={`benchmark-request-peers-btn${
                isRequestFlow ? " is-active" : ""
              }`}
              onClick={() =>
                handleBenchmarkTypeChange(
                  isRequestFlow ? "industry_average" : requestPeerType.id,
                )
              }
              title={
                isRequestFlow
                  ? "Back to benchmark analysis"
                  : requestPeerType.description
              }
              aria-pressed={isRequestFlow}
            >
              {isRequestFlow ? (
                <CloseRoundedIcon fontSize="small" />
              ) : (
                <GroupsOutlinedIcon fontSize="small" />
              )}
              <span className="benchmark-request-peers-btn__label">
                {isRequestFlow ? "Close request" : "Request Peers"}
              </span>
              {!isRequestFlow ? (
                <ArrowForwardRoundedIcon
                  className="benchmark-request-peers-btn__arrow"
                  fontSize="small"
                />
              ) : null}
            </button>
          ) : null}
        </div>
        <p className="benchmarking-header__subtitle">
          {isSiRating
            ? "Run the complete SI Rating from your SDM data and previous reports: KPI-level scoring against sector benchmarks, AA–FF grade, scoring workbook and rating report."
            : isRequestFlow
            ? "Select peer companies to request a custom benchmark. The GE3S team will process your request manually."
            : "Compare your sustainability index performance against sector peer, best-practice, previous-assessment and custom benchmarks. Identify gaps and prioritize improvements."}
        </p>
      </div>

      {isSiRating ? (
        <SiRatingPanel
          initialRatingId={searchParams.get("rating") || ""}
          onRatingSelected={(id) => setSearchParams({ mode: "si-rating", rating: id })}
        />
      ) : (
      <>
      <div
        className={`benchmarking-toolbar${
          isRequestFlow ? " benchmarking-toolbar--request" : ""
        }`}
      >
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="benchmark-report-label">Report</InputLabel>
          <Select
            labelId="benchmark-report-label"
            label="Report"
            value={selectValue}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedReportId(id);
              navigate(`/benchmarking/${id}?type=${benchmarkType}`, { replace: true });
            }}
            disabled={reportsLoading}
          >
            {scoredReports.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name} ({r.year}){r.grade ? ` — ${r.grade}` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!isRequestFlow ? (
          <div className="benchmarking-type-grid">
            {analysisTypes.map((type) => (
              <button
                key={type.id}
                type="button"
                className={`benchmark-type-chip${benchmarkType === type.id ? " active" : ""}`}
                onClick={() => handleBenchmarkTypeChange(type.id)}
                title={type.description}
              >
                <strong>{type.shortLabel}</strong>
                <span>{type.description}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="benchmarking-toolbar__request-hint">
            Linked to the selected report for the peer benchmark request.
          </p>
        )}
      </div>

      {reportsLoading || reportLoading ? (
        <Loader />
      ) : listError && !scoredReports.length ? (
        <div className="benchmark-empty">
          <h3>Unable to load reports</h3>
          <p>{listError}</p>
        </div>
      ) : !scoredReports.length ? (
        <div className="benchmark-empty">
          <h3>No scored reports yet</h3>
          <p>Complete data entry and generate a scorecard to use benchmarking.</p>
        </div>
      ) : isRequestFlow ? (
        <RequestBenchmarkPanel report={requestReport} />
      ) : loading && !analysis ? (
        <Loader />
      ) : error ? (
        <div className="benchmark-empty">
          <p>{error}</p>
        </div>
      ) : analysis ? (
        <>
          <BenchmarkDashboard
            dashboard={dashboard}
            benchmarkLabel={analysis.benchmarkLabel}
            orgName={orgName}
          />

          <div className="benchmarking-summary-row">
            <div className="benchmark-summary-card">
              <h3>Performance gap</h3>
              <div className="score-value">
                {analysis.overall?.gap != null
                  ? `${analysis.overall.gap >= 0 ? "+" : ""}${analysis.overall.gap.toFixed(1)}`
                  : "—"}
              </div>
              <div className={`score-gap ${gapClass}`}>
                {gapClass === "ahead"
                  ? "Ahead of benchmark"
                  : gapClass === "behind"
                    ? "Below benchmark — improvement opportunity"
                    : "On par with benchmark"}
              </div>
            </div>
            <div className="benchmark-summary-card">
              <h3>Context</h3>
              <div style={{ fontSize: "0.8rem", color: "#374151", lineHeight: 1.6 }}>
                <div>
                  <strong>Sector:</strong>{" "}
                  {asDisplayLabel(analysis.orgContext?.sector, "—")}
                </div>
                <div>
                  <strong>Country:</strong>{" "}
                  {asDisplayLabel(analysis.orgContext?.country, "—")}
                </div>
              </div>
            </div>
          </div>

          {showCustomPanel && (
            <div style={{ marginBottom: "1.5rem" }}>
              <CustomBenchmarkPanel
                onApply={(sections) => {
                  setCustomSectionTargets(sections);
                }}
              />
            </div>
          )}

          <div className="benchmarking-grid-2">
            <div className="benchmark-panel">
              <h2>ESG pillar comparison</h2>
              <SectionBarComparison sections={analysis.sections} />
            </div>
            <div className="benchmark-panel" style={{ minHeight: 300 }}>
              <h2>Score visualization</h2>
              <div style={{ height: 280 }}>
                <SectionComparisonChart sections={analysis.sections} />
              </div>
            </div>
          </div>

          <div className="benchmarking-grid-2">
            <div className="benchmark-panel">
              <h2>KPI comparison & gap analysis</h2>
              <Tabs
                value={kpiTab}
                onChange={(_, v) => setKpiTab(v)}
                sx={{ mb: 2, minHeight: 36 }}
                variant="scrollable"
              >
                {KPI_SECTION_TABS.map((t) => (
                  <Tab key={t.id} label={t.label} value={t.id} sx={{ minHeight: 36, fontSize: "0.75rem" }} />
                ))}
              </Tabs>
              <KpiComparisonTable kpis={analysis.kpis} filterSection={kpiTab} />
            </div>
            <div className="benchmark-panel">
              <h2>Actionable insights</h2>
              <BenchmarkInsights insights={analysis.insights} />
            </div>
          </div>
        </>
      ) : listError ? (
        <div className="benchmark-empty">
          <p>{listError}</p>
        </div>
      ) : (
        <div className="benchmark-empty">
          <p>Unable to load benchmark analysis for this report.</p>
        </div>
      )}
      </>
      )}
    </div>
  );
}
